import { FlexBox } from '@airship-platform/jam-web/components';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';

export const NotificationPreferencesSchema = z.object({
  notificationPreferences: z.object({
    appointmentReminder: z.boolean(),
    viewProposalNotification: z.boolean(),
    acceptProposalNotification: z.boolean(),
  }),
});

type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

const NOTIFICATION_PREFERENCES: {
  name: keyof NotificationPreferences['notificationPreferences'];
  label: string;
  description: string;
}[] = [
  {
    name: 'appointmentReminder',
    label: 'Appointment reminders',
    description: 'Get notified about upcoming appointments',
  },
  {
    name: 'viewProposalNotification',
    label: 'Proposal viewed',
    description: 'Notify when a customer views a proposal',
  },
  {
    name: 'acceptProposalNotification',
    label: 'Proposal accepted',
    description: 'Notify when a customer accepts a proposal',
  },
];

const NotificationPreferencesInput: React.FC = () => {
  const { control } = useFormContext<NotificationPreferences>();

  return (
    <FlexBox flexDirection="column" gap={2} width="100%">
      <Typography variant="subtitle1">Notifications</Typography>
      {NOTIFICATION_PREFERENCES.map(({ name, label, description }) => (
        <Controller
          key={name}
          name={`notificationPreferences.${name}`}
          control={control}
          render={({ field }) => (
            <FlexBox
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              width="100%"
            >
              <FlexBox flexDirection="column">
                <Typography variant="body2">{label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {description}
                </Typography>
              </FlexBox>
              <Switch
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                color="primary"
                slotProps={{ input: { 'aria-label': label } }}
              />
            </FlexBox>
          )}
        />
      ))}
    </FlexBox>
  );
};

export default NotificationPreferencesInput;
